import type { SpinResult } from '@aerofruits/game-sdk';

export type LightningTier =
  | 'BOOST_0_5'
  | 'BOOST_1_5'
  | 'BOOST_2_5'
  | 'BOOST_4_5'
  | 'BOOST_9_5'
  | 'BOOST_19_5';

export const LIGHTNING_TIER_ORDER: LightningTier[] = [
  'BOOST_0_5',
  'BOOST_1_5',
  'BOOST_2_5',
  'BOOST_4_5',
  'BOOST_9_5',
  'BOOST_19_5',
];

export const SECTOR_LABEL: Record<LightningTier, string> = {
  BOOST_0_5: '+0.5x',
  BOOST_1_5: '+1.5x',
  BOOST_2_5: '+2.5x',
  BOOST_4_5: '+4.5x',
  BOOST_9_5: '+9.5x',
  BOOST_19_5: '+19.5x',
};

export interface LightningRoundDetail {
  selectedNumber: number;
  dice: number[];
  matchCount: number;
  baseMultiplierTenths: number;
  lightningTriggered: boolean;
  lightningTier: LightningTier | null;
  lightningMultiplierTenths: number;
  totalMultiplierTenths: number;
  payoutMinor: number;
}

const TIERS = new Set<string>(LIGHTNING_TIER_ORDER);

export function parseLightningResult(result: SpinResult): LightningRoundDetail | null {
  const block = result.lightning_dice;
  const dice = block?.dice ?? result.dice;
  if (!dice || dice.length !== 3) return null;
  const selectedNumber = block?.selected_number ?? result.selected_number ?? 0;
  const matchCount = block?.match_count ?? result.match_count ?? 0;
  const tierRaw = block?.lightning_tier ?? result.lightning_tier ?? '';
  const lightningTier = TIERS.has(tierRaw) ? (tierRaw as LightningTier) : null;
  return {
    selectedNumber,
    dice: [...dice],
    matchCount,
    baseMultiplierTenths: block?.base_multiplier_tenths ?? result.base_multiplier_tenths ?? 0,
    lightningTriggered: block?.lightning_triggered ?? result.lightning_triggered ?? matchCount > 0,
    lightningTier,
    lightningMultiplierTenths:
      block?.lightning_multiplier_tenths ?? result.lightning_multiplier_tenths ?? 0,
    totalMultiplierTenths:
      block?.total_multiplier_tenths ?? result.total_multiplier_tenths ?? 0,
    payoutMinor: block?.payout_minor ?? result.payout_minor ?? result.total_win_minor,
  };
}

/** Formats tenths as a decimal multiplier label, e.g. 20 -> x2.0, 105 -> x10.5 */
export function formatMultiplierTenths(tenths: number): string {
  return `x${(tenths / 10).toFixed(1)}`;
}

/**
 * Resolves authoritative multiplier fields to tenths for display.
 * Legacy dice rounds store whole multipliers (3 = x3.0); Lightning rounds use tenths.
 */
export function resolveMultiplierTenths(
  multiplier: number,
  opts?: { totalMultiplierTenths?: number | undefined; lightning?: boolean | undefined },
): number {
  if (opts?.totalMultiplierTenths !== undefined) return opts.totalMultiplierTenths;
  if (opts?.lightning) return multiplier;
  return multiplier * 10;
}

/** Formats a Lightning bonus in +X.Xx form. */
export function formatLightningBonusTenths(tenths: number): string {
  return `+${(tenths / 10).toFixed(1)}x`;
}
